import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { track } from "../lib/mixpanel"
import { formatShortCurrency, formatCurrency } from "../lib/chart_formatters"

// Phase 2.2 Tier 3 — S-curve for the "By Schedule Dates" and
// "Forecast" tabs. Kept separate from eva_controller.js so Chart.js
// isn't pulled into the link-only Tier 1 flows. Mirrors the
// portfolio_trend_charts_controller pattern: lazy init when the
// canvas becomes visible, multi-line datasets, formatter-based
// tooltip/legend.
//
// The controller expects its series blob as JSON in
// `data-eva-charts-series-value` and an optional
// `data-eva-charts-grain-value` for axis labelling. The series shape
// is what `BudgetsHelper#eva_chart_series_json` returns.
export default class extends Controller {
  static targets = ["canvas"]
  static values = { series: Object, grain: String }

  connect() {
    this._chart = null
    this._renderedFired = false

    if (!this.hasCanvasTarget) return

    // Only init when the canvas is actually visible — Chart.js needs
    // a sized element. A parent `.hidden` (inactive tab) means we
    // wait for the IntersectionObserver to fire.
    if (this._isVisible()) {
      this._init()
    } else {
      this._observe()
    }
  }

  disconnect() {
    this._destroy()
    this._observer?.disconnect()
    this._observer = null
  }

  _isVisible() {
    const el = this.canvasTarget
    if (!el.isConnected) return false
    const rect = el.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  _observe() {
    if (typeof IntersectionObserver === "undefined") {
      // Fall back to a short retry loop for environments without IO.
      // Don't block more than a few frames.
      requestAnimationFrame(() => this._init())
      return
    }
    this._observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        this._observer.disconnect()
        this._observer = null
        this._init()
      }
    }, { threshold: 0.1 })
    this._observer.observe(this.canvasTarget)
  }

  _init() {
    if (this._chart) return
    const s = this.seriesValue
    if (!s || !Array.isArray(s.buckets) || s.buckets.length === 0) return

    const labels = s.buckets.map(iso => this._formatLabel(iso, s.grain || this.grainValue))
    const datasets = []

    datasets.push({
      label: "PV",
      data: s.pv,
      borderColor: "#9ca3af",
      backgroundColor: "transparent",
      borderWidth: 1.5,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 4,
      tension: 0.2,
      spanGaps: true
    })

    datasets.push({
      label: "EV",
      data: s.ev,
      borderColor: "#10b981",
      backgroundColor: "transparent",
      borderWidth: 2,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 4,
      tension: 0.2,
      spanGaps: true
    })

    datasets.push({
      label: "AC",
      data: s.ac,
      borderColor: "#ef4444",
      backgroundColor: "transparent",
      borderWidth: 2,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 4,
      tension: 0.2,
      spanGaps: true
    })

    if (s.has_baseline && Array.isArray(s.baseline_pv) && s.baseline_pv.length > 0) {
      datasets.push({
        label: "Baseline PV",
        data: s.baseline_pv,
        borderColor: "#6366f1",
        backgroundColor: "transparent",
        borderDash: [6, 4],
        borderWidth: 1.25,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.2,
        spanGaps: true
      })
    }

    const todayIdx = this._todayIndex(s.buckets)
    const endIdx = s.buckets.length - 1
    const verticalLines = this._verticalLinesPlugin([
      todayIdx >= 0 ? { index: todayIdx, color: "#2563eb", label: "Today" } : null,
      endIdx >= 0 ? { index: endIdx, color: "#f97316", label: "End" } : null
    ].filter(Boolean))

    this._chart = new Chart(this.canvasTarget, {
      type: "line",
      data: { labels, datasets },
      plugins: [verticalLines],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 6,
              boxHeight: 6,
              font: { size: 13 },
              usePointStyle: true
            }
          },
          tooltip: {
            callbacks: {
              label: ctx => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`
            }
          }
        },
        scales: {
          y: {
            ticks: { callback: v => formatShortCurrency(v), font: { size: 13 } },
            grid: { color: "#f3f4f6" }
          },
          x: { ticks: { font: { size: 13 } }, grid: { display: false } }
        }
      }
    })

    this._fireRendered()
  }

  _destroy() {
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
  }

  // Emits "EVA Chart Rendered" once the canvas has drawn. Payload
  // carries the grain and series-length so we can spot projects
  // where the chart is tiny (one-bucket) vs fleshed-out.
  _fireRendered() {
    if (this._renderedFired) return
    this._renderedFired = true
    try {
      const s = this.seriesValue || {}
      track("EVA Chart Rendered", {
        grain: s.grain || this.grainValue,
        bucket_count: (s.buckets || []).length,
        has_baseline: !!s.has_baseline
      })
    } catch { /* mixpanel optional */ }
  }

  _formatLabel(iso, grain) {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const year = d.getUTCFullYear()
    const month = d.getUTCMonth()
    if (grain === "quarterly") {
      const q = Math.floor(month / 3) + 1
      return `Q${q} ${year}`
    }
    if (grain === "weekly") {
      return `${d.toISOString().slice(5, 10)}`  // MM-DD — compact for dense axes
    }
    return d.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })
  }

  _todayIndex(bucketIsoList) {
    const now = new Date()
    const todayMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    for (let i = 0; i < bucketIsoList.length; i++) {
      const ms = Date.parse(bucketIsoList[i])
      if (!Number.isNaN(ms) && ms >= todayMs) return i
    }
    return bucketIsoList.length - 1
  }

  // Custom Chart.js plugin — draws dashed vertical lines at specific
  // x-axis indices (today + project end). Keeps us off the
  // chartjs-plugin-annotation dependency.
  _verticalLinesPlugin(lines) {
    return {
      id: "evaVerticalLines",
      afterDatasetsDraw(chart) {
        const { ctx, chartArea, scales } = chart
        if (!chartArea) return
        lines.forEach(({ index, color, label }) => {
          const x = scales.x.getPixelForValue(index)
          if (x == null) return
          ctx.save()
          ctx.strokeStyle = color
          ctx.setLineDash([3, 3])
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(x, chartArea.top)
          ctx.lineTo(x, chartArea.bottom)
          ctx.stroke()
          ctx.setLineDash([])
          ctx.fillStyle = color
          ctx.font = "11px system-ui, -apple-system, sans-serif"
          ctx.textAlign = "center"
          ctx.fillText(label, x, chartArea.top - 4)
          ctx.restore()
        })
      }
    }
  }

}
